import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, RotateCcw } from '../../icons';
// 채택 3단계 ③ 근태 — 화면 머리·표·팝업·입력을 정본 공통으로(A3 · E1 · C1 · B1).
import {
  Button, Checkbox, FormField, Input, Modal, ModalActions,
  PageHeader, Select, Table, type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listWorkSchedules, createWorkSchedule, updateWorkSchedule, saveWorkAssignments,
  WORK_TYPE_LABEL,
  // 근무요일 (판 2) — 기본값·이름·검사를 API 모듈 한 곳에서 가져온다(서버와 같은 잣대).
  DEFAULT_WORKDAYS, WEEKDAY_LABELS, workdaysValid, workdaysText,
  type WorkScheduleDto, type WorkAssignmentDto, type WorkScheduleInput, type AssignmentScope,
} from '../../api/hr-schedule';
import { listHireDates, type HireDateEmployeeDto } from '../../api/hr-admin';
import { crossesMidnight } from '../../lib/shift-roster';
import {
  WORK_SCHEDULE_TEMPLATES, templatesToCreate, type WorkScheduleTemplate,
} from '../../lib/work-schedule-templates';
import '../../styles/master-screen.css';
import './AttendanceAdmin.css';
import './ShiftPreset.css';
import './WorkSchedulePage.css';

/**
 * 관리 — **근무제와 배정** (근태 2단계 2026-08-24 / 기준 화면 2026-09-02).
 *
 * 설계: `docs/attendance-round2-sample.html` 제안 2 · 디자인 정본 P3-1.
 *
 * 여기서 정한 값이 곧 **지각 기준**이 된다. 판정 사다리는 개인 배정 → 팀 배정 → 회사 기본 →
 * 코드 09:00 이고, **배정이 하나도 없으면 오늘과 같은 09:00** 이라 근무제를 만들어 두기만 해서는
 * 아무 판정도 바뀌지 않는다 — 바꾸는 것은 **배정**이다.
 *
 * ★ 배정은 덮어쓰지 않는다. 같은 대상의 열린 배정을 닫고 새 줄을 쌓는다(서버가 한다) —
 *   덮어쓰면 **지난 달 판정이 소급해** 달라진다.
 *
 * ── 2026-09-02 · 기준 화면(정본 P3-1) ────────────────────────────────────
 * 직위·직급(#920) · 직책(#931)이 세운 배치를 근무제가 따른다. 배치는 공용
 * `styles/master-screen.css`(`.wc-master__*`) 한 벌이고 여기서 새로 그리지 않는다.
 *   · 화면 머리 = 제목 + 도움말 + 요약 칩 + 대표 행동 1 (`PageHeader`)
 *   · 왼쪽 264px = **담을 것**(표준 묶음)  · 오른쪽 = **본표**(우리 회사의 근무제)
 *   · 등록 · 수정 · 배정 = **팝업**. 표 위아래에 넣기 줄을 상주시키지 않는다
 *
 * ★★★ **판정 사다리는 도움말로 옮겼다**(정본 P8). 「원래 그런 규칙」은 화면에 두지 않는다 —
 *    다만 **「지금의 판정」은 화면에 남는다**: 배정이 0건이면 표가 지금 몇 시로 판정하는지
 *    그 자리에서 적는다. 규칙과 현재 상태는 다른 것이다.
 * ★★★ **중지가 없었다.** 지우는 API 가 없고 화면에도 끄는 길이 없어, 안 쓰게 된 근무제가
 *    배정 목록의 선택지에 영원히 남았다. 지우지 않고 **중지**한다 — 지난 배정이 그 이름을
 *    가리키고 있어서 없애면 지난 판정을 읽을 수 없다(직책·직위와 같은 규칙).
 * ★★ **말을 정본 P7 로 맞췄다** — 「만들기」→**등록**, 「고치기」→**수정**.
 */

const EMPTY_FORM: WorkScheduleInput = {
  name: '', type: 'STANDARD', startTime: '09:00', endTime: '18:00',
  breakMinutes: 60, workMinutes: 480, graceMinutes: 0,
  coreStart: null, coreEnd: null, flexStart: null, flexEnd: null,
  isDefault: false, isActive: true, sortOrder: 100,
  // ★ 판 2 — 기본은 월~금. 종전에 코드에 박혀 있던 「토·일은 쉰다」와 같은 답이다.
  workdays: DEFAULT_WORKDAYS,
};

const ERRORS: Record<string, string> = {
  FLEX_WINDOW_REQUIRED: '시차출퇴근은 선택폭(시작·끝)이 있어야 합니다 — 폭이 없으면 기준 시각이 안 나옵니다.',
  FLEX_WINDOW_INVALID: '선택폭의 시작이 끝보다 늦습니다.',
  WORKDAYS_INVALID: '근무요일을 하루 이상 골라야 합니다.',
  CORE_INVALID: '코어타임의 시작이 끝보다 늦습니다.',
  USER_REQUIRED: '개인 배정에는 사람이 필요합니다.',
  TEAM_REQUIRED: '팀 배정에는 팀이 필요합니다.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
};

const TYPE_OPTIONS = Object.entries(WORK_TYPE_LABEL).map(([value, label]) => ({ value, label }));
const SCOPE_OPTIONS = [
  { value: 'USER', label: '개인' },
  { value: 'TEAM', label: '팀' },
  { value: 'INSTITUTION', label: '회사 전체' },
];
const SCOPE_LABEL: Record<string, string> = { USER: '개인', TEAM: '팀', INSTITUTION: '회사' };

/** 열려 있는 팝업 — 등록·수정은 한 폼을 쓴다(`editing` 이 있으면 수정). */
type OpenModal =
  | { kind: 'schedule' }
  | { kind: 'adopt' }
  | { kind: 'assign' }
  /** 사람이 걸려 있는 근무제를 중지할 때만 연다 — 무엇이 · 몇 명이 · 되돌릴 수 있는지. */
  | { kind: 'stop'; row: WorkScheduleDto };

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 근무제 한 줄을 폼 값으로 — **PATCH 는 통째로 덮어쓴다**(빠뜨린 칸은 서버 기본값이 된다). */
function toForm(s: WorkScheduleDto): WorkScheduleInput {
  return {
    name: s.name, type: s.type, startTime: s.startTime, endTime: s.endTime,
    breakMinutes: s.breakMinutes, workMinutes: s.workMinutes, graceMinutes: s.graceMinutes,
    coreStart: s.coreStart, coreEnd: s.coreEnd, flexStart: s.flexStart, flexEnd: s.flexEnd,
    isDefault: s.isDefault, isActive: s.isActive, sortOrder: s.sortOrder,
    // ★ 옛 서버는 이 칸을 안 준다 — 그때는 월~금(종전 동작).
    workdays: s.workdays ?? DEFAULT_WORKDAYS,
  };
}

export function WorkSchedulePage(): JSX.Element {
  const [schedules, setSchedules] = useState<WorkScheduleDto[]>([]);
  const [assignments, setAssignments] = useState<WorkAssignmentDto[]>([]);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [employees, setEmployees] = useState<HireDateEmployeeDto[]>([]);
  const [editing, setEditing] = useState<WorkScheduleDto | null>(null);
  const [modal, setModal] = useState<OpenModal | null>(null);
  const [form, setForm] = useState<WorkScheduleInput>(EMPTY_FORM);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  /**
   * 표준 묶음의 **고칠 수 있는 사본** — 회사마다 시각이 다르다(교대에서 이미 겪었다).
   * 목록에서 고르고, 등록 팝업 안에서 시각을 고쳐 넣는다.
   */
  const [drafts, setDrafts] = useState<WorkScheduleTemplate[]>(
    () => WORK_SCHEDULE_TEMPLATES.map((t) => ({ ...t })),
  );
  const [assignDraft, setAssignDraft] = useState<{
    scope: AssignmentScope; scheduleId: string; userId: string; teamId: string; effectiveFrom: string;
  }>({ scope: 'USER', scheduleId: '', userId: '', teamId: '', effectiveFrom: todayIso() });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await listWorkSchedules();
    setSchedules(data.schedules);
    setAssignments(data.assignments);
    setTeams(data.teams);
    setAssignDraft((d) => ({ ...d, scheduleId: d.scheduleId || data.schedules[0]?.id || '' }));
  }, []);

  useEffect(() => {
    let alive = true;
    load()
      .catch((err) => {
        if (!alive) return;
        const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
        setError(ERRORS[code] ?? '근무제를 불러오지 못했습니다.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [load]);

  useEffect(() => {
    // 개인 배정에 쓸 사람 목록 — 직원 목록 API 를 그대로 쓴다(새 목록을 만들지 않는다).
    listHireDates().then((d) => setEmployees(d.employees)).catch(() => { /* 배정만 못 할 뿐 화면은 산다 */ });
  }, []);

  /** 한 자리에서만 오류를 말한다 — 팝업이 열려 있으면 그 안에서. */
  const run = async (tag: string, fn: () => Promise<void>, fail: string) => {
    setBusy(tag); setError(null); setDone(null);
    try { await fn(); await load(); }
    catch (err) {
      const res = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
      const known = ERRORS[res?.data?.code ?? ''] ?? (res?.status === 403 ? ERRORS.FORBIDDEN : null);
      setError(known ?? fail);
    }
    finally { setBusy(null); }
  };

  const startNew = () => {
    setEditing(null); setForm(EMPTY_FORM); setError(null); setModal({ kind: 'schedule' });
  };
  const startEdit = (s: WorkScheduleDto) => {
    setEditing(s); setForm(toForm(s)); setError(null); setModal({ kind: 'schedule' });
  };

  /** 등록 · 수정 — 한 폼이 두 일을 한다. 무엇을 하는 중인지는 `editing` 이 가른다. */
  const submit = () => run('save', async () => {
    const name = form.name.trim();
    if (!name) { setError('근무제 이름을 적어 주세요.'); return; }
    if (editing) {
      await updateWorkSchedule(editing.id, { ...form, name });
      setDone(`«${name}» 을 저장했습니다.`);
    } else {
      await createWorkSchedule({ ...form, name });
      setDone(`«${name}» 을 등록했습니다 — 배정하거나 회사 기본으로 지정해야 판정이 바뀝니다.`);
    }
    setModal(null); setEditing(null); setForm(EMPTY_FORM);
  }, editing ? '저장하지 못했습니다.' : '등록하지 못했습니다.');

  /**
   * ★★ 고른 것을 **한 번에** 등록한다. 이미 있는 이름은 건너뛰므로 두 번 눌러도 겹치지 않는다.
   * ★ 하나가 실패해도 나머지는 만든다 — 셋 만들다 둘째에서 멈추면 반쪽만 남는다.
   */
  const adopt = () => run('adopt', async () => {
    const todo = templatesToCreate(pickedFresh, schedules);
    if (todo.length === 0) { setDone('이미 다 있습니다 — 보탤 것이 없었습니다.'); setModal(null); return; }
    const made: string[] = [];
    const failed: string[] = [];
    for (const t of todo) {
      try {
        await createWorkSchedule({
          name: t.name, type: t.type,
          startTime: t.startTime, endTime: t.endTime,
          breakMinutes: t.breakMinutes, workMinutes: t.workMinutes, graceMinutes: t.graceMinutes,
          coreStart: t.coreStart, coreEnd: t.coreEnd,
          flexStart: t.flexStart, flexEnd: t.flexEnd,
          sortOrder: t.sortOrder,
        });
        made.push(t.name);
      } catch {
        // 한 줄이 막혀도 나머지는 만든다. 무엇이 됐고 무엇이 안 됐는지는 아래에서 말한다.
        failed.push(t.name);
      }
    }
    if (made.length === 0) { setError('등록하지 못했습니다.'); return; }
    setDone(`${made.length}개를 등록했습니다: ${made.join(' · ')}`
      + (failed.length > 0 ? ` (${failed.join(' · ')} 은 등록하지 못했습니다)` : '')
      + ' — 배정해야 판정이 바뀝니다.');
    setModal(null); setPicked(new Set());
  }, '등록하지 못했습니다.');

  /**
   * 중지 · 사용 — 지우지 않는다. 지난 기록이 그 이름을 가리키고 있다.
   *
   * ★★ PATCH 는 **통째로 덮어쓴다** — 한 칸만 보내면 나머지가 서버 기본값(휴게 60 · 소정 480 …)으로
   *    조용히 되돌아간다. 그래서 지금 줄을 통째로 실어 보낸다.
   * ★★★ **중지는 «새 배정만» 막는 것이 아니다.** 서버는 판정할 때 근무제를 `isActive: true` 로만
   *    읽으므로(`work-policy.service.ts`), 중지하면 **그 근무제로 배정된 사람도** 사다리에서
   *    한 칸 내려가 **회사 기본(없으면 09:00)** 으로 판정된다. 사람이 걸려 있으면 먼저 묻는 이유다.
   */
  const toggleActive = (s: WorkScheduleDto) => run(`t${s.id}`, async () => {
    await updateWorkSchedule(s.id, { ...toForm(s), isActive: !s.isActive });
    setDone(s.isActive
      ? `«${s.name}» 을 중지했습니다`
        + (s.assignedCount > 0
          ? ` — 걸려 있던 배정 ${s.assignedCount}건은 이제 ${defaultName ? `«${defaultName}»(${nowBase})` : nowBase} 로 판정됩니다.`
          : ' — 새로 배정할 수 없습니다(지난 기록은 그대로).')
      : `«${s.name}» 을 다시 사용합니다.`);
    setModal(null);
  }, '바꾸지 못했습니다.');

  /**
   * 중지를 누를 때 — **사람이 걸려 있으면 먼저 묻는다.** 걸린 것이 없으면 바로 한다
   *   (아무 일도 안 일어나는 확인창은 사람을 확인창에 무디게 만든다 · 정본 P4).
   */
  const askStop = (s: WorkScheduleDto) => {
    if (s.assignedCount > 0) { setError(null); setModal({ kind: 'stop', row: s }); return; }
    void toggleActive(s);
  };

  const saveAssignment = () => run('assign', async () => {
    await saveWorkAssignments([{
      scope: assignDraft.scope,
      scheduleId: assignDraft.scheduleId,
      userId: assignDraft.scope === 'USER' ? assignDraft.userId : null,
      teamId: assignDraft.scope === 'TEAM' ? assignDraft.teamId : null,
      effectiveFrom: assignDraft.effectiveFrom,
    }]);
    setDone('배정했습니다 — 이전 배정은 하루 전으로 닫혔습니다(지난 판정은 그대로).');
    setModal(null);
  }, '배정하지 못했습니다.');

  const scheduleName = useMemo(
    () => new Map(schedules.map((s) => [s.id, s.name])),
    [schedules],
  );

  /** 표준 묶음 — 이미 있는 이름은 «있음» 으로 지워 둔다(다시 담을 것이 없다). */
  const marked = useMemo(
    () => drafts.map((t) => ({ ...t, taken: schedules.some((s) => s.name.trim() === t.name.trim()) })),
    [drafts, schedules],
  );
  const fresh = marked.filter((t) => !t.taken);
  const pickedFresh = fresh.filter((t) => picked.has(t.name));

  const defaultName = schedules.find((s) => s.isDefault && s.isActive)?.name ?? null;
  /** 배정이 하나도 없을 때 **지금** 무엇으로 판정하는가 — 규칙이 아니라 현재 상태다. */
  const nowBase = schedules.find((s) => s.isDefault && s.isActive)?.startTime ?? '09:00';

  const patchDraft = (name: string, over: Partial<WorkScheduleTemplate>) =>
    setDrafts((prev) => prev.map((t) => (t.name === name ? { ...t, ...over } : t)));

  const isFlex = form.type === 'FLEX';
  const isSelect = form.type === 'SELECT';

  const columns: ReadonlyArray<TableColumn<WorkScheduleDto>> = [
    {
      key: 'name', header: '근무제',
      render: (s) => (
        <span className="wcsch__nmwrap">
          <b className={`wcsch__nm${s.isActive ? '' : ' wcsch__nm--off'}`}>{s.name}</b>
          {s.isDefault && <span className="wcsch__badge">회사 기본</span>}
        </span>
      ),
    },
    { key: 'type', header: '종류', render: (s) => WORK_TYPE_LABEL[s.type] ?? s.type },
    {
      key: 'time', header: '시업–종업',
      render: (s) => (
        <span className="wcsch__time">
          {s.startTime}–{s.endTime}
          {crossesMidnight(s.startTime, s.endTime) && <span className="shp__over">자정 넘음</span>}
        </span>
      ),
    },
    { key: 'breakMinutes', header: '휴게(분)', numeric: true },
    { key: 'workMinutes', header: '소정(분)', numeric: true },
    { key: 'graceMinutes', header: '유예(분)', numeric: true },
    {
      /* ★ 판 2 — 설정해 놓고 어디서도 안 보이면 안 한 것과 같다. 표에서 한눈에 읽힌다. */
      key: 'workdays', header: '근무요일',
      render: (s) => workdaysText(s.workdays),
    },
    {
      /* ★ 배정 수를 표에 세운다 — 중지하려는 근무제에 사람이 걸려 있는지 «누르기 전에» 보인다. */
      key: 'assignedCount', header: '배정', numeric: true,
      render: (s) => (s.assignedCount > 0 ? `${s.assignedCount}건` : '—'),
    },
    {
      key: 'active', header: '상태',
      render: (s) => (
        <span className={`wc-master__st${s.isActive ? ' is-on' : ''}`}>{s.isActive ? '사용' : '중지'}</span>
      ),
    },
    {
      /* ★ 행 작업은 «늘 보이게» 둔다 — `rowActions` 는 hover 로만 나타난다(정본 P5 위반). */
      key: 'act', header: <span className="wc-master__sr">작업</span>, align: 'right',
      render: (s) => (
        <span className="wcsch__acts">
          <Button
            variant="secondary" size="sm"
            aria-label={`${s.name} 수정`}
            onClick={() => startEdit(s)}
            iconLeft={<Pencil size={13} aria-hidden />}
          >
            수정
          </Button>
          {s.isActive ? (
            <Button
              variant="quiet-danger" size="sm"
              aria-label={`${s.name} 중지`}
              disabled={busy === `t${s.id}`}
              onClick={() => askStop(s)}
            >
              중지
            </Button>
          ) : (
            <Button
              variant="secondary" size="sm"
              aria-label={`${s.name} 사용`}
              disabled={busy === `t${s.id}`}
              onClick={() => toggleActive(s)}
              iconLeft={<RotateCcw size={13} aria-hidden />}
            >
              사용
            </Button>
          )}
        </span>
      ),
    },
  ];

  const assignColumns: ReadonlyArray<TableColumn<WorkAssignmentDto>> = [
    { key: 'scope', header: '범위', render: (a) => SCOPE_LABEL[a.scope] ?? a.scope },
    { key: 'target', header: '대상', render: (a) => a.userName ?? a.teamName ?? '전 직원' },
    { key: 'schedule', header: '근무제', render: (a) => scheduleName.get(a.scheduleId) ?? a.scheduleId },
    { key: 'effectiveFrom', header: '시행일' },
    {
      key: 'effectiveTo', header: '종료일',
      render: (a) => (
        <span className={a.effectiveTo ? 'wcsch__closed' : undefined}>{a.effectiveTo ?? '진행 중'}</span>
      ),
    },
  ];

  /** 배정 팝업의 «저장» 이 열리는 조건 — 범위마다 없으면 안 되는 칸이 다르다. */
  const assignReady = Boolean(assignDraft.scheduleId)
    && (assignDraft.scope !== 'USER' || Boolean(assignDraft.userId))
    && (assignDraft.scope !== 'TEAM' || Boolean(assignDraft.teamId));

  return (
    <div className="hra wcsch">
      {/* ── 화면 머리 — 제목 · 도움말 · 요약 · 대표 행동 (정본 P3-1 · A3) ────
             ★ 판정 사다리는 「원래 그런 규칙」이라 도움말이 맡는다(정본 P8). */}
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/work-schedules')}
        scope={scopeFor('/erp/attendance/work-schedules')}
        title="근무제"
        help={{
          title: '근무제와 지각 기준',
          body: (
            <>
              · <b>판정 사다리</b> — 개인 배정 → 팀 배정 → 회사 기본 근무제 → 코드 기본값 09:00.
              위에서부터 처음 만나는 것을 씁니다.<br />
              · <b>배정이 하나도 없으면 09:00</b> 입니다 — 근무제를 등록해 두기만 해서는
              판정이 바뀌지 않습니다. 바꾸는 것은 <b>배정</b>이거나 <b>회사 기본</b> 지정입니다.<br />
              · 퇴근(종업) 시각도 같은 사다리에서 나옵니다 — 시업이 09:30 이면 종업은 18:30.<br />
              · 배정은 덮어쓰지 않고 <b>이전 것을 닫은 뒤 쌓습니다</b> — 덮어쓰면 지난 달 판정이
              소급해 달라집니다.<br />
              · 지우지 않고 <b>중지</b>합니다. 중지하면 새로 배정할 수 없을 뿐 아니라,
              <b>그 근무제로 이미 배정된 사람도 사다리에서 한 칸 내려가</b> 회사 기본(없으면 09:00)으로
              판정됩니다. 지난 기록은 그대로 남고, 언제든 <b>사용</b>으로 되돌릴 수 있습니다.
            </>
          ),
        }}
        actions={
          <>
            {/* 2026-10-14 사용자 지시 — 화면 머리 오른쪽의 건수(사용 중 · 배정)를 지웠다(메일과 같은 규칙). */}
            <Button
              variant="primary" size="compact" onClick={startNew}
              iconLeft={<Plus size={14} aria-hidden />}
            >
              근무제 등록
            </Button>
          </>
        }
      />

      {error && !modal && <p className="wc-master__err" role="alert">{error}</p>}
      {done && !error && <p className="wc-master__ok" role="status">{done}</p>}

      <div className="wc-master__two">
        {/* ── 왼쪽 — 담을 것 ── */}
        <div className="wc-master__side">
          <section className="wc-master__panel" aria-label="표준 묶음">
            <div className="wc-master__panelh">
              <span className="wc-master__panelt">표준 묶음</span>
              <span className="wc-master__paneln">골라서 등록한다</span>
            </div>
            <div className="wc-master__panelb">
              <ul className="wc-master__pick">
                {marked.map((t) => {
                  const on = picked.has(t.name);
                  return (
                    <li key={t.name} className={t.taken ? 'is-taken' : (on ? 'is-on' : undefined)}>
                      {/* ★ `Checkbox` 가 스스로 `<label>` 이다 — 밖을 라벨로 또 감싸면
                             읽어 주는 쪽이 무엇을 고르는지 잃는다. 줄 전체를 라벨 «안» 에 넣는다. */}
                      <Checkbox
                        className="wc-master__pickrow"
                        checked={on} disabled={t.taken}
                        onChange={() => setPicked((p) => {
                          const next = new Set(p);
                          if (next.has(t.name)) next.delete(t.name); else next.add(t.name);
                          return next;
                        })}
                        label={(
                          <>
                            <span className="wc-master__pickname">{t.name}</span>
                            {t.taken && <span className="wc-master__have">있음</span>}
                            {/* ★ 곁값에 «종류» 를 적으면 넷은 이름과 같은 낱말이 두 번 선다
                                   (통상근무 · 시차출퇴근 · 선택근로 · 탄력근로). 가르는 것은
                                   **시각**이다 — 소정 0분인 휴무만 시각 대신 그 사실을 적는다. */}
                            <span className="wc-master__picksub">
                              {t.workMinutes === 0 ? '휴무' : `${t.startTime}–${t.endTime}`}
                            </span>
                          </>
                        )}
                      />
                    </li>
                  );
                })}
              </ul>
              <div className="wc-master__pickfoot">
                <Button
                  variant="secondary" size="compact"
                  disabled={pickedFresh.length === 0 || busy === 'adopt'}
                  onClick={() => { setError(null); setModal({ kind: 'adopt' }); }}
                >
                  {pickedFresh.length > 0 ? `${pickedFresh.length}개 등록` : '등록'}
                </Button>
                {fresh.length > 0 && (
                  <Button
                    variant="ghost" size="compact"
                    onClick={() => setPicked(new Set(fresh.map((t) => t.name)))}
                  >
                    모두 고르기
                  </Button>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* ── 오른쪽 — 본표 ── */}
        <section className="wc-master__panel wc-master__main" aria-label="근무제표">
          <div className="wc-master__panelh">
            <span className="wc-master__panelt">근무제표</span>
            <span className="wc-master__paneln">
              {/* ★ 「지금의 판정」은 화면에 적는다 — 규칙과 현재 상태는 다른 것이다. */}
              {defaultName
                ? `회사 기본은 «${defaultName}» · 시업 ${nowBase}`
                : '회사 기본이 없다 — 배정이 없는 사람은 09:00'}
            </span>
          </div>
          <Table
            caption="근무제표"
            className="wcsch__tbl"
            columns={columns}
            rows={schedules}
            rowKey={(s) => s.id}
            loading={loading}
            empty={(
              <>왼쪽 <b>표준 묶음</b>에서 골라 등록하거나, 오른쪽 위 <b>근무제 등록</b>으로 시작합니다.</>
            )}
          />
        </section>
      </div>

      {/* ── 배정 — 판정을 실제로 바꾸는 자리 ── */}
      <section className="wc-master__panel" aria-label="배정">
        <div className="wc-master__panelh">
          <span className="wc-master__panelt">배정</span>
          <span className="wc-master__paneln">위 칸(개인)이 아래 칸(팀·회사)을 이긴다</span>
          <span className="wc-master__sp" />
          <Button
            variant="secondary" size="compact"
            disabled={schedules.length === 0}
            onClick={() => { setError(null); setModal({ kind: 'assign' }); }}
            iconLeft={<Plus size={14} aria-hidden />}
          >
            배정 추가
          </Button>
        </div>
        <Table
          caption="배정 현황"
          className="wcsch__atbl"
          columns={assignColumns}
          rows={assignments}
          rowKey={(a) => a.id}
          loading={loading}
          empty={(
            <>배정이 없습니다 — 지금은 모두 <b>{nowBase}</b> 로 판정합니다.</>
          )}
        />
      </section>

      {/* ── 등록 · 수정 팝업 ── */}
      {modal?.kind === 'schedule' && (
        <Modal
          size="md"
          title={editing ? `근무제 수정 — ${editing.name}` : '근무제 등록'}
          onClose={() => { setModal(null); setEditing(null); }}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => { setModal(null); setEditing(null); }}>취소</Button>
              <Button
                variant="primary"
                onClick={submit}
                disabled={busy === 'save' || !form.name.trim()
                  || !workdaysValid(form.workdays ?? DEFAULT_WORKDAYS)}
              >
                {editing ? '저장' : '등록'}
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          {/* ★★ 한 줄에 선 칸은 «도움말 유무»만이 아니라 **도움말이 몇 줄이냐**로도 높이가 갈린다.
                 #932 는 앞의 것(있음/없음 → 18px)이었고, 여기서 실브라우저로 잰 것은 뒤의 것이다:
                 칸이 161px 이라 열두 자를 넘으면 도움말이 두 줄로 접혀 입력 상자가 내려앉는다.
                 → **한 줄에 서는 칸의 도움말은 한 줄에 들어가게 짧게 쓴다.** 길게 할 말은 도움말
                 아이콘(P8)의 몫이다. */}
          <div className="wc-master__form">
            <FormField label="이름" required hint="배정에서 고르는 이름">
              <Input
                value={form.name} autoFocus maxLength={60}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormField>
            <FormField label="종류" hint="칸이 종류마다 다르다">
              <Select
                value={form.type}
                options={TYPE_OPTIONS}
                onChange={(v) => setForm({ ...form, type: v })}
              />
            </FormField>
            <FormField label="시업" hint="곧 지각 기준">
              <Input
                type="time" value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              />
            </FormField>
            <FormField label="종업" hint="시업 따라 움직인다">
              <Input
                type="time" value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </FormField>
            <FormField label="휴게(분)" className="wc-master__narrow">
              <Input
                value={String(form.breakMinutes ?? 60)} inputMode="numeric"
                onChange={(e) => setForm({ ...form, breakMinutes: Number(e.target.value.replace(/[^0-9]/g, '')) })}
              />
            </FormField>
            <FormField label="소정(분)" className="wc-master__narrow">
              <Input
                value={String(form.workMinutes ?? 480)} inputMode="numeric"
                onChange={(e) => setForm({ ...form, workMinutes: Number(e.target.value.replace(/[^0-9]/g, '')) })}
              />
            </FormField>
            <FormField label="지각 유예(분)" className="wc-master__narrow">
              <Input
                value={String(form.graceMinutes ?? 0)} inputMode="numeric"
                onChange={(e) => setForm({ ...form, graceMinutes: Number(e.target.value.replace(/[^0-9]/g, '')) })}
              />
            </FormField>
            {/*
              ★★★ 판 2 — **근무요일**. 종전에는 이 칸이 없어서 시스템이 어느 날이 근무일인지
                 몰랐고, 팀 근태가 「토·일」을 코드에 박아 쓰고 있었다. 주 6일 회사, 화~토 회사가
                 실제로 있다 — 고르는 칸이 없으면 그들은 적을 길이 없다.
              ★ 자리는 **월요일부터**다(서버 문자열과 같은 순서).
            */}
            {/*
              ★★★ 판 2 — **근무요일**. 종전에는 이 칸이 없어서 시스템이 어느 날이 근무일인지
                 몰랐고, 팀 근태가 「토·일」을 코드에 박아 쓰고 있었다. 주 6일 회사, 화~토 회사가
                 실제로 있다 — 고르는 칸이 없으면 그들은 적을 길이 없다.
              ★★★ **묶음은 `<fieldset>` + `<legend>`** — 정본 B5(체크박스) 규격이고 RadioGroup 이
                 선례다. `FormField label=` 을 쓰지 않는 이유는 따로 있다: FormField 가 컨트롤 id 를
                 컨텍스트로 내려 주므로 **체크박스 일곱이 같은 id 를 갖게** 되고 라벨을 눌러도
                 첫 칸만 반응한다.
              ★ 자리는 **월요일부터**다(서버 문자열과 같은 순서).
            */}
            <fieldset className="wc-master__wide wcsch__daysrow">
              <legend className="wcsch__dayslabel">근무요일</legend>
              <div className="wcsch__days">
                {WEEKDAY_LABELS.map((label, i) => (
                  <Checkbox
                    key={label}
                    label={label}
                    checked={(form.workdays ?? DEFAULT_WORKDAYS)[i] === '1'}
                    onChange={(e) => {
                      const cur = (form.workdays ?? DEFAULT_WORKDAYS).split('');
                      cur[i] = e.target.checked ? '1' : '0';
                      setForm({ ...form, workdays: cur.join('') });
                    }}
                  />
                ))}
              </div>
              {!workdaysValid(form.workdays ?? DEFAULT_WORKDAYS) && (
                <p className="wcsch__daysbad" role="status">
                  하루 이상 골라야 합니다 — 하나도 없으면 이 근무제를 받은 사람은 근무일이 없습니다.
                </p>
              )}
            </fieldset>
            {isFlex && (
              <>
                <FormField label="선택폭 시작" required>
                  <Input
                    type="time" value={form.flexStart ?? ''}
                    onChange={(e) => setForm({ ...form, flexStart: e.target.value || null })}
                  />
                </FormField>
                <FormField label="선택폭 끝" required>
                  <Input
                    type="time" value={form.flexEnd ?? ''}
                    onChange={(e) => setForm({ ...form, flexEnd: e.target.value || null })}
                  />
                </FormField>
              </>
            )}
            {isSelect && (
              <>
                <FormField label="코어 시작">
                  <Input
                    type="time" value={form.coreStart ?? ''}
                    onChange={(e) => setForm({ ...form, coreStart: e.target.value || null })}
                  />
                </FormField>
                <FormField label="코어 끝">
                  <Input
                    type="time" value={form.coreEnd ?? ''}
                    onChange={(e) => setForm({ ...form, coreEnd: e.target.value || null })}
                  />
                </FormField>
              </>
            )}
            <div className="wc-master__wide">
              <Checkbox
                checked={Boolean(form.isDefault)}
                label="회사 기본 — 배정이 없는 사람이 쓰는 값(하나뿐)"
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
              />
            </div>
          </div>
          <p className="wc-master__mnote">
            {isFlex && <><b>시차출퇴근</b>은 선택폭이 필수입니다 — 고르지 않은 날은 선택폭의 마지막 시각이 기준이 됩니다.{' '}</>}
            {isSelect && <><b>선택근로</b>는 코어타임을 비우면 완전선택근로제가 되어 지각 판정을 하지 않습니다.{' '}</>}
            {form.type === 'ELASTIC' && <><b>탄력근로</b>의 주 단위 배정표는 아직 없습니다 — 지금은 시업 시각으로 판정합니다.{' '}</>}
            {form.type === 'SHIFT' && (
              <>
                <b>교대근무는 한 줄이 조(組) 하나</b>입니다 — 주간·야간·휴무는 왼쪽 <b>표준 묶음</b>에서
                한 번에 등록하고, 누가 어느 날 어느 조인지는 <b>교대 편성</b> 화면에서 정합니다.{' '}
              </>
            )}
            {form.type === 'STANDARD' && <>등록만으로는 판정이 바뀌지 않습니다 — <b>배정</b>하거나 <b>회사 기본</b>으로 지정해야 합니다.</>}
          </p>
        </Modal>
      )}

      {/* ── 표준 묶음 등록 확인 — 시각을 여기서 고쳐 넣는다 ── */}
      {modal?.kind === 'adopt' && (
        <Modal
          /* ★ 칸이 여섯(이름·종류·시업·종업·휴게·소정)이라 md(720)에는 안 들어간다 —
                「탄력근 / 로」처럼 종류가 세로로 쪼개진다(실브라우저에서 잡았다). */
          size="lg"
          title="표준 묶음 등록"
          onClose={() => setModal(null)}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => setModal(null)}>취소</Button>
              <Button variant="primary" onClick={adopt} disabled={busy === 'adopt'}>등록</Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          <p className="wc-master__mlead">{pickedFresh.length}개를 근무제표에 등록합니다.</p>
          {/* ★★ **시각은 여기서 고친다.** 회사마다 다르다 — 교대에서 이미 겪은 것을
                 종류 전체로 넓혔다(등록하고 나서 한 줄씩 다시 여는 것보다 빠르다). */}
          <table className="shp__table wcsch__ptbl">
            <thead>
              <tr>
                <th scope="col">이름</th>
                <th scope="col">종류</th>
                <th scope="col">시업</th>
                <th scope="col">종업</th>
                <th scope="col">휴게(분)</th>
                <th scope="col">소정(분)</th>
              </tr>
            </thead>
            <tbody>
              {pickedFresh.map((t) => (
                <tr key={t.name}>
                  <td>
                    <b>{t.name}</b>
                    <div className="wc-master__picksub">{t.note}</div>
                  </td>
                  <td>{WORK_TYPE_LABEL[t.type] ?? t.type}</td>
                  <td>
                    <input
                      type="time" aria-label={`${t.name} 시업`} value={t.startTime}
                      onChange={(e) => patchDraft(t.name, { startTime: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time" aria-label={`${t.name} 종업`} value={t.endTime}
                      onChange={(e) => patchDraft(t.name, { endTime: e.target.value })}
                    />
                    {crossesMidnight(t.startTime, t.endTime) && <span className="shp__over">자정 넘음</span>}
                  </td>
                  <td>
                    <input
                      type="number" className="hra__num" aria-label={`${t.name} 휴게`} value={t.breakMinutes}
                      onChange={(e) => patchDraft(t.name, { breakMinutes: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    {/* ★ 여기에 「휴무」 꼬리표를 또 달면 좁은 칸에서 「휴 / 무」로 쪼개진다.
                           같은 말을 이름 칸의 곁글이 이미 하고 있다 — 한 번만 적는다. */}
                    <input
                      type="number" className="hra__num" aria-label={`${t.name} 소정`} value={t.workMinutes}
                      onChange={(e) => patchDraft(t.name, { workMinutes: Number(e.target.value) })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="wc-master__mnote">
            <b>이미 있는 이름은 건너뜁니다.</b> 회사가 고쳐 둔 시각을 표준값으로 되돌리지 않습니다.
            <b> 휴무 조는 소정을 0분</b>으로 둡니다 — 그날 지각·결근을 묻지 않습니다.
            종업이 시업보다 이른 조(22:00→07:00)는 <b>자정을 넘는 것</b>으로 봅니다.
          </p>
        </Modal>
      )}

      {/* ── 중지 확인 — 무엇이 · 몇 명이 · 되돌릴 수 있는지 셋을 적는다 (정본 P4) ── */}
      {modal?.kind === 'stop' && (
        <Modal
          size="sm"
          title={`근무제 중지 — ${modal.row.name}`}
          onClose={() => setModal(null)}
          footer={(
            <ModalActions>
              {/* ★ 되돌릴 수 있는 일이라 기본 포커스를 취소로 보내지 않는다 —
                     `danger: true` 의 그 규칙은 «되돌릴 수 없는 것» 의 규칙이다. */}
              <Button variant="secondary" onClick={() => setModal(null)}>취소</Button>
              <Button
                variant="danger"
                disabled={busy === `t${modal.row.id}`}
                onClick={() => toggleActive(modal.row)}
              >
                중지
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          <p className="wc-master__mlead">
            «{modal.row.name}» 에 배정 <b>{modal.row.assignedCount}건</b>이 걸려 있습니다.
          </p>
          <p className="wc-master__mnote">
            중지하면 그 사람들의 판정이 <b>{defaultName ? `«${defaultName}» — 시업 ${nowBase}` : `${nowBase}`}</b> 로
            바뀝니다. 배정 줄과 지난 기록은 <b>지워지지 않고</b> 그대로 남으며, 언제든 <b>사용</b>으로
            되돌리면 판정도 함께 돌아옵니다.
          </p>
        </Modal>
      )}

      {/* ── 배정 팝업 ── */}
      {modal?.kind === 'assign' && (
        <Modal
          size="sm"
          title="배정 추가"
          onClose={() => setModal(null)}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => setModal(null)}>취소</Button>
              <Button variant="primary" onClick={saveAssignment} disabled={busy === 'assign' || !assignReady}>
                배정
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          <div className="wc-master__form">
            <FormField label="범위">
              <Select
                value={assignDraft.scope}
                options={SCOPE_OPTIONS}
                onChange={(v) => setAssignDraft({ ...assignDraft, scope: v as AssignmentScope })}
              />
            </FormField>
            {assignDraft.scope === 'USER' && (
              <FormField label="사람" required>
                {/* ★ 글자로 걸러낼 수 있어야 한다 — 목록이 길면 101번째 사람은 고를 수 없다. */}
                <Select
                  value={assignDraft.userId || null} searchable placeholder="고르기"
                  options={employees.map((e) => ({
                    value: e.userId, label: e.teamName ? `${e.name} · ${e.teamName}` : e.name,
                  }))}
                  onChange={(v) => setAssignDraft({ ...assignDraft, userId: v })}
                />
              </FormField>
            )}
            {assignDraft.scope === 'TEAM' && (
              <FormField label="팀" required>
                <Select
                  value={assignDraft.teamId || null} searchable placeholder="고르기"
                  options={teams.map((t) => ({ value: t.id, label: t.name }))}
                  onChange={(v) => setAssignDraft({ ...assignDraft, teamId: v })}
                />
              </FormField>
            )}
            <FormField label="근무제" required>
              <Select
                value={assignDraft.scheduleId || null} searchable placeholder="고르기"
                /* ★ 중지한 근무제는 새로 배정할 수 없다 — 목록에서 뺀다(지난 배정은 그대로). */
                options={schedules.filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name }))}
                onChange={(v) => setAssignDraft({ ...assignDraft, scheduleId: v })}
              />
            </FormField>
            <FormField label="시행일">
              <Input
                type="date" value={assignDraft.effectiveFrom}
                onChange={(e) => setAssignDraft({ ...assignDraft, effectiveFrom: e.target.value })}
              />
            </FormField>
          </div>
          <p className="wc-master__mnote">
            같은 대상의 <b>열린 배정은 시행일 하루 전으로 닫히고</b> 새 줄이 쌓입니다 —
            덮어쓰지 않으므로 <b>지난 달 판정은 그대로</b>입니다.
          </p>
        </Modal>
      )}
    </div>
  );
}
